import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as moment from "moment";
import PropTypes from "prop-types";
import { Button } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import {
  useGetGeneralParameters,
  useGetMainCurrencyByCountryId,
  useGetMultiUserCurrenciesByCountryId,
} from "@hooks/useGeneralParams";
import QuoteInfoAndCondition from "./components/QuoteInfoAndCondition";
import RelatedContactList from "./components/RelatedContactList";
import QuoteSpecificCondition from "../QuoteView/components/QuoteSpecificCondition";
import QuoteServices from "./components/QuoteServices";
import {
  OuterWrapperBackground,
  PaddingDiv,
  ButtonWrapper,
  GridRow,
  OuterDiv,
} from "./AddEditQuote.styled";

const AddEditQuote = (props) => {
  const {
    t,
    selectedQuote,
    quoteId,
    isEditMode,
    countryId,
    siteId,
    companyId,
    setRefreshState,
    siteData,
  } = props;

  const [calculateMargin, setCalculateMargin] = useState(false);
  const [statusDraft, setStatusDraft] = useState(false);
  const [statusQuote, setStatusQuote] = useState(false);
  const [isQuoteCreating, setIsQuoteCreating] = useState(false);
  const [createQuoteAPIError, setCreateQuoteAPIError] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactErrorMessage, setContactErrorMessage] = useState("");
  const [itemsErrorMessage, setItemsErrorMessage] = useState("");
  const [existingRelatedContact, setExistingRelatedContact] = useState([]);
  const [conditionData, setConditionData] = useState([]);
  const [selectedSellingPrice, setSelectedSellingPrice] = useState([]);
  const [requestStatus, setRequestStatus] = useState([]);
  const [exchangeCurrency, setExchangeCurrency] = useState([]);
  const [exchangeRate, setExchangeRate] = useState([]);
  const [additionalCondition, setAdditionalCondition] = useState(
    selectedQuote?.additionalSpecificConditions ?? ""
  );
  const [serviceList, setServiceList] = useState([]);
  const generalParams = useGetGeneralParameters();

  const { countryCurrencyPosition } = generalParams;
  const [userCurrencyObject, setUserCurrencyObject] = useState(
    useGetMultiUserCurrenciesByCountryId(countryId)
  );
  const [mainCurrencyId, setMainCurrencyId] = useState(
    useGetMainCurrencyByCountryId(countryId)
  );

  const currencyList = generalParams.countryCurrency;

  const history = useNavigate();

  useEffect(() => {
    if (!mainCurrencyId && Object.keys(generalParams).length > 0) {
      setUserCurrencyObject(useGetMultiUserCurrenciesByCountryId(countryId));
      setMainCurrencyId(useGetMainCurrencyByCountryId(countryId));
    }
  }, [generalParams]);

  const [formData, setFormData] = useState({
    id: {
      required: false,
      value: selectedQuote?.id ?? "",
      error: "",
    },
    name: {
      required: false,
      value: selectedQuote?.name ?? "",
      error: "",
    },
    taxRate: {
      required: false,
      value: selectedQuote?.taxRate?.calculationRate ?? "",
      error: "",
    },
    taxRateId: {
      required: false,
      value: selectedQuote?.taxRate?.id ?? "",
      error: "",
    },
    winningProbability: {
      required: false,
      value: selectedQuote?.winningProbability ?? "",
      error: "",
    },
    expirationDate: {
      required: false,
      value: selectedQuote?.expirationDate
        ? moment(selectedQuote.expirationDate).format("YYYY-MM-DD")
        : moment().add(1, "M").format("YYYY-MM-DD"),
      error: "",
    },
    estimatedValidationDate: {
      required: false,
      value: selectedQuote?.estimatedValidationDate
        ? moment(selectedQuote.estimatedValidationDate).format("YYYY-MM-DD")
        : "",
      error: "",
    },
    paymentMethod: {
      required: false,
      value: selectedQuote?.paymentMethod?.id ?? "",
      error: "",
    },
    paymentCondition: {
      required: false,
      value: selectedQuote?.paymentCondition?.id ?? "",
      error: "",
    },
    paymentTerms: {
      required: false,
      value: selectedQuote?.paymentTerms?.id ?? "",
      error: "",
    },
    currency: {
      required: false,
      value: selectedQuote?.currency?.id ?? parseInt(mainCurrencyId, 10),
      error: "",
    },
    contacts: {
      required: false,
      value: selectedContacts ?? [],
      error: "",
    },
    additionalSpecificConditions: {
      required: false,
      value: selectedQuote?.additionalSpecificConditions ?? "",
      error: "",
    },
    totalExcludingTax: {
      required: false,
      value: selectedQuote?.totalExcludingTax ?? "",
      error: "",
    },
    totalIncludingTax: {
      required: false,
      value: selectedQuote?.totalIncludingTax ?? "",
      error: "",
    },
    totalMarginRate: {
      required: false,
      value: selectedQuote?.totalMarginRate ?? "",
      error: "",
    },
    totalMargin: {
      required: false,
      value: selectedQuote?.totalMargin ?? "",
      error: "",
    },
  });

  useEffect(() => {
    setFormData({
      ...formData,
      additionalSpecificConditions: {
        value: additionalCondition,
        error: "",
        required: true,
      },
    });
  }, [additionalCondition]);

  useEffect(() => {
    if (isEditMode) {
      if (parseInt(selectedQuote.status, 10) !== 1) {
        setStatusDraft(true);
      }
      if (
        parseInt(selectedQuote.status, 10) !== 1 &&
        parseInt(selectedQuote.status, 10) !== 2
      ) {
        setStatusQuote(true);
      }
      if (selectedQuote?.contacts && selectedQuote?.contacts.length > 0) {
        const contactList = [];
        const relatedContactList = [];
        selectedQuote?.contacts.map((contactInfo) => {
          contactList.push(contactInfo.contact);
          relatedContactList.push({
            relatedContactId: contactInfo.id,
            contactId: contactInfo.contact.id,
          });
        });
        setSelectedContacts(contactList);
        setExistingRelatedContact(relatedContactList);
      }
      if (selectedQuote?.services && selectedQuote?.services.length > 0) {
        const sellingPriceList = [];
        selectedQuote?.services.map((item) => {
          if (item?.sellingPrice?.id)
            sellingPriceList.push(item?.sellingPrice?.id);
        });
        setSelectedSellingPrice(sellingPriceList);
      }
      if (
        selectedQuote?.exchangeRates &&
        selectedQuote?.exchangeRates.length > 0
      ) {
        const rates = [];
        const excahngeCurrencyList = [];
        selectedQuote?.exchangeRates.map((exchange) => {
          rates.push({
            exchangeRateId: exchange?.id,
            id: exchange?.currencyFrom,
            rate: exchange.exchangeRate,
            error: "",
          });
          excahngeCurrencyList.push(parseInt(exchange?.currencyFrom, 10));
        });
        setExchangeRate([...rates]);
        setExchangeCurrency([...excahngeCurrencyList]);
      }
    }
  }, [isEditMode]);

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.name.value) {
      isFormValid = false;
      data.name.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.taxRateId.value) {
      isFormValid = false;
      data.taxRate.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.winningProbability.value && data.winningProbability.value !== 0) {
      isFormValid = false;
      data.winningProbability.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.estimatedValidationDate.value) {
      isFormValid = false;
      data.estimatedValidationDate.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.paymentMethod.value) {
      isFormValid = false;
      data.paymentMethod.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.paymentCondition.value) {
      isFormValid = false;
      data.paymentCondition.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.paymentTerms.value) {
      isFormValid = false;
      data.paymentTerms.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.currency.value) {
      isFormValid = false;
      data.currency.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (selectedContacts.length === 0) {
      isFormValid = false;
      setContactErrorMessage(t("QUOTE_ERROR_NO_CONTACT"));
    } else {
      setContactErrorMessage("");
    }
    if (serviceList.length === 0) {
      isFormValid = false;
      setItemsErrorMessage(t("QUOTE_ERROR_NO_SERVICES"));
    } else {
      setItemsErrorMessage("");
    }
    const responseData = serviceList.map((service) => {
      const tempData = { ...service };
      if (!service.label) {
        isFormValid = false;
        tempData.labelError = t("FIELD_REQUIRED");
      }
      service.items.map((item, itemIndex) => {
        if (!item.label) {
          isFormValid = false;
          tempData.items[itemIndex].labelError = t("FIELD_REQUIRED");
        }
      });
      return tempData;
    });

    let rates = [];
    if (exchangeCurrency?.length > 0) {
      if (exchangeRate?.length > 0) {
        rates = exchangeRate.map((rateInfo, raetIndex) => {
          const tempData = { ...rateInfo };
          if (!rateInfo.rate || rateInfo.rate === "") {
            isFormValid = false;
            tempData.error = t("FIELD_REQUIRED");
          }
          return tempData;
        });
      }
      if (exchangeRate?.length === 0) {
        exchangeCurrency.map((exchange) => {
          rates.push({ id: exchange, rate: "", error: t("FIELD_REQUIRED") });
        });
      }
    }
    if (!isFormValid) {
      setFormData({ ...data });
      setServiceList([...responseData]);
      if (exchangeRate?.length > 0) setExchangeRate([...rates]);
    }
    return isFormValid;
  };

  const getFormData = () => {
    const relatedContactListData = [];
    const quoteConditionsData = [];
    const quoteServiceData = [];
    const quoteExchangeData = [];

    conditionData.map((condition) => {
      condition.children.map((child) => {
        quoteConditionsData.push({
          id: child.childId ? child.childId : null,
          quoteId: isEditMode ? selectedQuote.id : "",
          specificCondition: { id: child.id },
          parametersValue: child.parametersValue ? child.parametersValue : null,
        });
      });
    });
    serviceList.map((service) => {
      const serviceDataObject = {
        id: service.id,
        label: service.label,
        sellingPrice: { id: service.sellingPriceId },
        currency: service.currency,
        catalog: { id: service.catalogId },
        items: [],
      };
      if (service?.items?.length > 0) {
        service?.items.map((item) => {
          const serviceItems = {
            id: item.id,
            label: item.label,
            hesusService: { id: item.hesusService?.id },
            hesusCatalogService: { id: item.hesusCatalogServiceId },
            sellingPriceItem: { id: item.sellingPriceItemId },
            quantity: item.quantity,
            itemSellingPrice: item.unitPrice,
            unit: item.unit,
            margin: item.marginRate,
            // margin: 0,
            sellingPriceExcludedTax: item.sellingPriceExcludedTax,
            sellingPriceIncludedTax: formData.taxRate.value
              ? parseFloat(item?.totalPrice) * (1 + formData.taxRate.value)
              : 0,
            vatRate: formData.taxRate.value,
            product: { id: item.product?.id },
            transport: { id: item?.transport?.id },
            service: requestStatus[service.enquiryId],
            description: item.comment,
          };
          serviceDataObject.items.push(serviceItems);
        });
      }
      quoteServiceData.push(serviceDataObject);
    });

    selectedContacts.map((selectedContact) => {
      const contactExist = selectedQuote?.contacts?.find(
        (contactList) =>
          parseInt(contactList.contact.id, 10) ===
          parseInt(selectedContact.id, 10)
      );
      if (contactExist) {
        relatedContactListData.push({
          id: contactExist?.id,
          entityId: selectedQuote?.id ?? "",
          entityType: 8,
          role: contactExist?.role,
          status: 1,
          contact: { id: contactExist?.contact?.id },
        });
      } else {
        relatedContactListData.push({
          id: "",
          entityId: selectedQuote?.id ?? "",
          entityType: 8,
          role: selectedContact?.job,
          status: 1,
          contact: { id: selectedContact.id },
        });
      }
    });

    if (exchangeRate.length > 0) {
      exchangeRate.map((rateInfo) => {
        quoteExchangeData.push({
          id: rateInfo?.exchangeRateId,
          currencyFrom: rateInfo.id,
          currencyTo: formData.currency.value,
          exchangeRate: rateInfo.rate,
        });
      });
    }
    const data = {
      id: isEditMode ? selectedQuote.id : "",
      name: formData.name.value,
      company: { id: companyId },
      site: { id: siteId },
      currency: { id: formData.currency.value },
      vatRate: { id: formData.taxRateId.value },
      winningProbability: formData.winningProbability.value,
      expirationDate: formData.expirationDate.value
        ? moment(formData.expirationDate.value).format("YYYY-MM-DD")
        : null,
      estimatedValidationDate: moment(
        formData.estimatedValidationDate.value
      ).format("YYYY-MM-DD"),
      paymentMethod: { id: formData.paymentMethod.value },
      paymentTerms: { id: formData.paymentTerms.value },
      paymentConditions: { id: formData.paymentCondition.value },
      totalExcludingTax: formData.totalExcludingTax.value,
      totalIncludingTax: formData.totalIncludingTax.value,
      totalMargin: formData.totalMargin.value,
      totalMarginRate: formData.totalMarginRate.value,
      contacts: relatedContactListData,
      specificConditions: quoteConditionsData,
      services: quoteServiceData,
      exchangeRates: quoteExchangeData,
      additionalSpecificConditions: formData.additionalSpecificConditions.value,
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsQuoteCreating(true);
      setCreateQuoteAPIError("");
      if (!quoteId && !isEditMode) {
        axios
          .post("/quotes", getFormData())
          .then((response) => {
            setIsQuoteCreating(false);
            setRefreshState(true);
            history(`/site/view/${siteId}/quote-view-${response?.data[0].id}`);
          })
          .catch((error) => {
            setIsQuoteCreating(false);
            setCreateQuoteAPIError(error.response?.data?.message ?? "");
          });
      }
      // Edit
      if (quoteId && isEditMode) {
        axios
          .put(`/quotes/${quoteId}`, getFormData())
          .then(() => {
            setIsQuoteCreating(false);
            setRefreshState(true);
            history(`/site/view/${siteId}/quote-view-${quoteId}`);
          })
          .catch((error) => {
            setIsQuoteCreating(false);
            setCreateQuoteAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  const onCancel = () => {
    if (!isEditMode) history(`/site/view/${siteId}/quotes`);
    else {
      history(`/site/view/${siteId}/quote-view-${quoteId}`);
    }
  };

  const openSpecificationConditionPopup = () => {
    setIsAddEditSpecificConditionPopup(true);
  };

  const getExchangePrice = (currencyId, price) => {
    const rateInfo = exchangeRate.find((rate) => rate.id === currencyId);
    return rateInfo ? rateInfo?.rate * price : price;
  };

  useEffect(() => {
    if (calculateMargin) {
      let excludedTax = 0;
      let includedTax = 0;
      let marginRate = 0;
      let marginValue = 0;
      let totalPrice = 0;
      let totalPurchasePrice = 0;

      serviceList.map((service) => {
        service.items.map((item) => {
          if (item.totalPrice) {
            excludedTax +=
              getExchangePrice(item?.currency?.id, item.unitPrice) *
              item.quantity;
          }

          if (item.calculateMarginRate) {
            // totalMarginRate += parseFloat(item.calculateMarginRate);
            totalPurchasePrice += parseFloat(
              getExchangePrice(item?.currency?.id, item?.purchasePrice) *
                item.quantity
            );
            totalPrice += parseFloat(
              getExchangePrice(item?.currency?.id, item.unitPrice) *
                item.quantity
            );
          }
        });
      });

      if (formData.taxRate.value || formData.taxRate.value === 0) {
        includedTax = parseInt(excludedTax, 10) * (1 + formData.taxRate.value);
      }
      if (totalPurchasePrice && totalPrice) {
        marginRate = parseFloat(
          ((excludedTax - totalPurchasePrice) / totalPrice) * 100
        ).toFixed(2);
        marginValue = (excludedTax * marginRate) / 100;
      }

      setFormData({
        ...formData,
        totalExcludingTax: {
          value: excludedTax,
          error: "",
          required: true,
        },
        totalIncludingTax: {
          value: includedTax,
          error: "",
          required: true,
        },
        totalMarginRate: {
          value: marginRate,
          error: "",
          required: true,
        },
        totalMargin: {
          value: marginValue,
          error: "",
          required: true,
        },
      });
    }
    setCalculateMargin(false);
  }, [calculateMargin]);

  return (
    <OuterWrapperBackground>
      <PaddingDiv>
        <QuoteInfoAndCondition
          formData={formData}
          setFormData={setFormData}
          countryId={countryId}
          statusDraft={statusDraft}
          statusQuote={statusQuote}
          setCalculateMargin={setCalculateMargin}
          userCurrency={userCurrencyObject}
        />
        <OuterDiv>
          <QuoteServices
            statusDraft={statusDraft}
            siteId={siteId}
            selectedSellingPrice={selectedSellingPrice}
            setSelectedSellingPrice={setSelectedSellingPrice}
            siteData={siteData}
            data={selectedQuote?.services}
            serviceList={serviceList}
            setServiceList={setServiceList}
            requestStatus={requestStatus}
            setRequestStatus={setRequestStatus}
            calculateMargin={calculateMargin}
            setCalculateMargin={setCalculateMargin}
            formData={formData}
            currencyPosition={countryCurrencyPosition[formData.currency.value]}
            currency={userCurrencyObject[formData.currency.value]}
            itemsErrorMessage={itemsErrorMessage}
            setExchangeCurrency={setExchangeCurrency}
            exchangeCurrency={exchangeCurrency}
            currencyList={currencyList}
            exchangeRate={exchangeRate}
            setExchangeRate={setExchangeRate}
          />
        </OuterDiv>
        <GridRow column="60% 1fr">
          <OuterDiv>
            <QuoteSpecificCondition
              conditionData={conditionData}
              setConditionData={setConditionData}
              data={selectedQuote?.specificConditions}
              additionalCondition={additionalCondition}
              setAdditionalCondition={setAdditionalCondition}
              formData={formData}
              page="create"
            />
          </OuterDiv>
          <RelatedContactList
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
            statusDraft={statusDraft}
            statusQuote={statusQuote}
            companyId={companyId}
            contactErrorMessage={contactErrorMessage}
            setContactErrorMessage={setContactErrorMessage}
            countryId={countryId}
          />
        </GridRow>
        <ButtonWrapper>
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onCancel}
          >
            {t("CANCEL")}
          </Button>
          {createQuoteAPIError && <div>{createQuoteAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isQuoteCreating && <ClipLoader size={15} color="#fff" />}
              {!isQuoteCreating && t("VALIDATE")}
            </>
          </Button>
        </ButtonWrapper>
      </PaddingDiv>
    </OuterWrapperBackground>
  );
};

AddEditQuote.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  isEditMode: PropTypes.bool,
};
AddEditQuote.defaultProps = {
  id: null,
  isEditMode: false,
};

export default withTranslation()(AddEditQuote);
